import {useQuery} from '@apollo/client';
import {Box, Stack, Text, Title} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import SectionTabs from 'ui-library/SectionTabs';

import type {GetScientificModels} from './__generated/GetScientificModels';
import {ScientificModelCard} from './ScientificModelCard';
import {GET_SCIENTIFIC_MODELS_QUERY} from './ScientificModelsQueries';

const MODEL_HUB_TAB = {
  MODELS: 'models',
} as const;

type ModelHubTab = (typeof MODEL_HUB_TAB)[keyof typeof MODEL_HUB_TAB];

export function ScientificModelsView(): JSX.Element {
  const [activeTab, setActiveTab] = React.useState<ModelHubTab>(MODEL_HUB_TAB.MODELS);
  const {data, loading, error} = useQuery<GetScientificModels>(GET_SCIENTIFIC_MODELS_QUERY);

  const renderModelsContent = (): JSX.Element => {
    if (loading) {
      return <Text>Loading models...</Text>;
    }

    if (error) {
      return <Text color="red">Error loading models: {error.message}</Text>;
    }

    const models = data?.scientificModels ?? [];

    return (
      <div className={css(styles.modelsGrid)}>
        {models.map((model) => (
          <ScientificModelCard
            key={model.systemName}
            systemName={model.systemName}
            displayName={model.displayName}
            iconPath={model.iconPath}
            description={model.description}
            paperLink={model.paperLink}
            inputTypes={model.inputTypes}
            category={model.category}
          />
        ))}
      </div>
    );
  };

  const tabs = [
    {
      id: 'model-hub-models-tab',
      ariaControls: 'model-hub-models-panel',
      text: 'Models',
      isActive: activeTab === MODEL_HUB_TAB.MODELS,
      handleClick: () => {
        setActiveTab(MODEL_HUB_TAB.MODELS);
      },
    },
  ];

  return (
    <Box className={css(styles.container)}>
      <Stack spacing="xl" style={{width: '100%'}}>
        <Title order={2}>Model Hub</Title>
        <div className={css(styles.tabsWrapper)}>
          <SectionTabs tabs={tabs} />
        </div>
        <div
          id="model-hub-models-panel"
          role="tabpanel"
          aria-labelledby="model-hub-models-tab"
          className={css(styles.tabPanel)}
        >
          {activeTab === MODEL_HUB_TAB.MODELS && renderModelsContent()}
        </div>
      </Stack>
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: '24px',
    width: '100%',
    maxWidth: '100%',
  },
  tabsWrapper: {
    marginTop: 8,
  },
  tabPanel: {
    marginTop: 8,
  },
  modelsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    width: '100%',
  },
});
